/* global process */
import { html } from 'lit';
import {
  ACCESS_WEEKLY_PERMISSION,
  BATCH_PERMISSION,
  RESEARCH_PERMISSION
} from './components/views/weekly-plan-check-view/common.ts';
import { devRoutes } from './dev/routes.ts';
import { DicomMode, DicomType } from './enums/Dicom.ts';
import type { RouteConfig } from './utils/router/Router.ts';

export const Route = {
  Root: 'root',
  WeeklyCheckLanding: 'weekly-check-landing',
  WeeklyCheckOrganization: 'weekly-check-organization',
  WeeklyCheckPatient: 'weekly-check-patient',
  NotFound404: '404-not-found',
  OrganizationSettingsMain: 'organization-settings-main-view',
  OrganizationSettingsSub: 'organization-settings-sub',
  Document: 'document',
  Image: 'image',
  DoseAccumulated: 'dose-accumulated',
  DoseDelivered: 'dose-delivered',
  Login: 'login',
  Logout: 'logout',
  AdminUsers: 'auth-users',
  AdminOrganizations: 'auth-organizations',
  AdminPermissions: 'auth-permissions',
  AdminRoot: 'landing-root',
  AdminLanding: 'landing-admin',
  ResetPassword: 'reset-password',
  ForgotPassword: 'forgot-password',
  BlankImage: 'images-not-loaded',
  Batch: 'batch',
  SupportImageViewer: 'support-skia-viewer',
  SupportImageViewerIndex: 'support-skia-viewer-index',
  SupportImportPatient: 'support-import-patient',
  SupportTimeTool: 'support-time-tool',
  ResearchLanding: 'research-landing',
  ResearchPatient: 'research-patient',
  EditDvf: 'edit-dvf'
} as const;

export type Route = ValueOf<typeof Route>;

export const routes: Record<string, RouteConfig> = {
  [Route.Root]: {
    route: '/',
    forward: Route.WeeklyCheckLanding
  },
  [Route.WeeklyCheckLanding]: {
    route: '/weekly-check',
    permissions: [ACCESS_WEEKLY_PERMISSION],
    view: () => import('./components/views/weekly-plan-check-view/weekly-plan-check-view.ts'),
    title: 'WeeklyCheck',
    render: () => html`<weekly-plan-check-view></weekly-plan-check-view>`
  },
  'organization-without-patient': {
    route: '/weekly-check/:organization',
    permissions: [ACCESS_WEEKLY_PERMISSION],
    forward: Route.WeeklyCheckOrganization
  },
  [Route.WeeklyCheckPatient]: {
    route: '/weekly-check/:organization/patient/:patientSer',
    permissions: [ACCESS_WEEKLY_PERMISSION],
    view: () => import('./components/views/weekly-plan-check-view/weekly-plan-check-view.ts'),
    title: 'WeeklyCheck',
    render: () => html`<weekly-plan-check-view></weekly-plan-check-view>`
  },
  [Route.WeeklyCheckOrganization]: {
    route: '/weekly-check/:organization/patient',
    permissions: [ACCESS_WEEKLY_PERMISSION],
    view: () => import('./components/views/weekly-plan-check-view/weekly-plan-check-view.ts'),
    title: 'WeeklyCheck',
    render: () => html`<weekly-plan-check-view></weekly-plan-check-view>`
  },
  [Route.OrganizationSettingsMain]: {
    route: '/weekly-check/:organization/settings',
    view: () => import('./components/views/settings-view/settings-view.ts'),
    title: 'WeeklyCheck Settings',
    render: () => html`<settings-view></settings-view>`
  },
  [Route.OrganizationSettingsSub]: {
    route: '/weekly-check/:organization/settings/:page',
    view: () => import('./components/views/settings-view/settings-view.ts'),
    title: 'WeeklyCheck Settings',
    render: () => html`<settings-view></settings-view>`
  },
  [Route.Document]: {
    route: '/weekly-check/:organization/document/:documentType/:documentId',
    view: () => import('./components/views/document-view/document-view.ts'),
    title: 'WeeklyCheck - Document',
    render: () => html`<document-view></document-view>`
  },
  [Route.Image]: {
    route: '/weekly-check/:organization/image/:patientSer/:planSer/:fraction/:index',
    view: () => import('./components/views/skia-view/skia-view.ts'),
    title: 'WeeklyCheck - Skia',
    render: () => html`<skia-view mode=${DicomMode.Image}></skia-view>`
  },
  [Route.DoseAccumulated]: {
    route: '/weekly-check/:organization/dose/accumulated/:patientSer/:planSer/:planSumSer/:index',
    view: () => import('./components/views/skia-view/skia-view.ts'),
    title: 'WeeklyCheck - Skia',
    render: () => html`<skia-view mode=${DicomMode.Dose} type=${DicomType.Accumulated}></skia-view>`
  },
  [Route.DoseDelivered]: {
    route: '/weekly-check/:organization/dose/delivered/:patientSer/:planSer/:fraction/:index',
    view: () => import('./components/views/skia-view/skia-view.ts'),
    title: 'WeeklyCheck - Skia',
    render: () => html`<skia-view mode=${DicomMode.Dose} type=${DicomType.Delivered}></skia-view>`
  },
  [Route.EditDvf]: {
    route: '/weekly-check/:organization/dose/delivered/:patientSer/:planSer/:fraction/:index/edit',
    view: () => import('./components/views/skia-view/skia-view.ts'),
    title: 'WeeklyCheck - Skia',
    render: () => html`<skia-view mode=${DicomMode.Dvf}></skia-view>`
  },
  [Route.NotFound404]: {
    notFound: true,
    route: '/error/404',
    view: () => import('./components/views/error-404-view.ts'),
    title: '404 Not Found',
    render: () => html`<error-404-view></error-404-view>`
  },
  [Route.Login]: {
    route: '/auth/login',
    view: () => import('./components/views/auth/login-view.ts'),
    title: 'Login',
    render: () => html`<login-view></login-view>`
  },
  [Route.Logout]: {
    route: '/auth/logout',
    forward: Route.Login
  },
  [Route.AdminRoot]: {
    route: '/auth',
    view: () => import('./components/views/auth/users-view.ts'),
    title: 'Admin - Users',
    render: () => html`<users-view></users-view>`
  },
  [Route.AdminLanding]: {
    route: '/auth/:organization',
    forward: Route.AdminUsers
  },
  [Route.AdminUsers]: {
    route: '/auth/:organization/users',
    view: () => import('./components/views/auth/users-view.ts'),
    title: 'Admin - Users',
    render: () => html`<users-view></users-view>`
  },
  [Route.AdminOrganizations]: {
    route: '/auth/:organization/organizations',
    view: () => import('./components/views/auth/organizations-view.ts'),
    title: 'Admin - Organizations',
    render: () => html`<organizations-view></organizations-view>`
  },
  [Route.AdminPermissions]: {
    route: '/auth/:organization/permissions',
    view: () => import('./components/views/auth/permissions-view.ts'),
    title: 'Admin - Permissions',
    render: () => html`<permissions-view></permissions-view>`
  },
  [Route.ResetPassword]: {
    route: '/auth/password/reset',
    view: () => import('./components/views/auth/password-reset-view.ts'),
    title: 'Password Reset',
    render: () => html`<password-reset-view></password-reset-view>`
  },
  [Route.ForgotPassword]: {
    route: '/auth/password/forgot',
    view: () => import('./components/views/auth/password-forgot-view.ts'),
    title: 'Forgot Password',
    render: () => html`<password-forgot-view></password-forgot-view>`
  },
  [Route.BlankImage]: {
    route: '/weekly-check/:organization/image/:patientSer/blank',
    view: () => import('./components/views/blank-image-view.ts'),
    title: 'Images Not Loaded',
    render: () => html`<blank-image-view></blank-image-view>`
  },
  [Route.Batch]: {
    route: '/weekly-check/:organization/batch',
    permissions: [BATCH_PERMISSION],
    view: () => import('./components/views/batch-view/batch-view.ts'),
    title: 'Batch Approval',
    render: () => html`<batch-view></batch-view>`
  },
  [Route.SupportImageViewer]: {
    route: '/support/image',
    view: () => import('./support/support-skia-viewer/support-skia-viewer.ts'),
    title: 'Skia Image Viewer',
    render: () => html`<support-skia-viewer></support-skia-viewer>`
  },
  [Route.SupportImageViewerIndex]: {
    route: '/support/image/:index',
    view: () => import('./support/support-skia-viewer/support-skia-viewer.ts'),
    title: 'Skia Image Viewer',
    render: () => html`<support-skia-viewer></support-skia-viewer>`
  },
  [Route.SupportTimeTool]: {
    route: '/support/time',
    view: () => import('./support/time-tool-view.ts'),
    title: 'Time Testing Tool',
    render: () => html`<time-tool></time-tool>`
  },
  [Route.ResearchLanding]: {
    route: '/weekly-check/:organization/research',
    permissions: [RESEARCH_PERMISSION],
    view: () => import('./components/views/weekly-plan-check-view/weekly-plan-check-view.ts'),
    title: 'Research Review',
    render: () => html`<weekly-plan-check-view researchMode></weekly-plan-check-view>`
  },
  'research-without-patientSer': {
    route: '/weekly-check/:organization/research/patient',
    permissions: [RESEARCH_PERMISSION],
    forward: Route.ResearchLanding
  },
  [Route.ResearchPatient]: {
    route: '/weekly-check/:organization/research/patient/:patientSer',
    permissions: [RESEARCH_PERMISSION],
    view: () => import('./components/views/weekly-plan-check-view/weekly-plan-check-view.ts'),
    title: 'WeeklyCheck',
    render: () => html`<weekly-plan-check-view researchMode></weekly-plan-check-view>`
  },
  [Route.SupportImportPatient]: {
    route: '/support/import',
    view: () => import('./support/import-patient-view.ts'),
    title: 'Import Patient',
    render: () => html`<import-patient-view></import-patient-view>`
  }
} as const;

if (process.env.NODE_ENV !== 'production') {
  Object.assign(routes, devRoutes);
}
